import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  HairlineCard,
  Icons,
  iconSize,
  Link,
  palette,
  Stack,
  StatusChip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  type StatusKind,
} from '@munifocus/ui';
import type { SafetyEventStatus, SafetyEventView, SafetyTimerView } from '@munifocus/contracts-gov';
import { AppShell } from '../components/AppShell';
import { ModuleOffNotice } from '../components/ModuleOffNotice';
import { PageIntro } from '../components/PageIntro';
import { useOptionalScope } from '../providers/ScopeProvider';
import { useRealtime } from '../lib/realtime';
import { useMutationToast } from '../lib/mutation-toast';
import {
  formatCountdown,
  mapsLink,
  shortTime,
  useAcknowledge,
  useResolve,
  useSafetyBoard,
} from '../lib/safety-api';

const EVENT_KIND: Record<SafetyEventStatus, StatusKind> = {
  open: 'emergency',
  acknowledged: 'warning',
  resolved: 'success',
};

function useNow(everyMs = 1_000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), everyMs);
    return () => clearInterval(id);
  }, [everyMs]);
  return now;
}

function Location({ event }: { event: SafetyEventView }) {
  const { t, i18n } = useTranslation();
  const loc = event.location;
  if (!loc) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t('gov.safety.board.noLocation')}
      </Typography>
    );
  }
  return (
    <Typography variant="body2" color="text.secondary">
      {t('gov.safety.board.location')} ({t(`gov.safety.board.locationSource.${loc.source}`)}
      {loc.accuracy != null
        ? `, ${t('gov.safety.board.accuracy', { meters: Math.round(loc.accuracy) })}`
        : ''}
      , {shortTime(loc.at, i18n.language)}){' '}
      <Link href={mapsLink(loc.lat, loc.lng)} target="_blank" rel="noopener noreferrer">
        {t('gov.safety.board.openMap')}
      </Link>
    </Typography>
  );
}

function EventCard({
  event,
  onResolve,
}: {
  event: SafetyEventView;
  onResolve: (event: SafetyEventView) => void;
}) {
  const { t, i18n } = useTranslation();
  const toast = useMutationToast();
  const ack = useAcknowledge();
  const resolved = event.status === 'resolved';
  return (
    <HairlineCard
      component="article"
      aria-label={`${t(`gov.safety.kind.${event.kind}`)} — ${event.officerName}`}
      sx={{
        p: 2,
        borderLeft: `4px solid ${
          event.status === 'open' ? palette.semantic.emergency : palette.border.line
        }`,
      }}
    >
      <Stack direction="row" sx={{ justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography sx={{ fontWeight: 700 }}>
            {t(`gov.safety.kind.${event.kind}`)} — {event.officerName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('gov.safety.board.raisedAt', { time: shortTime(event.raisedAt, i18n.language) })}
            {event.escalationLevel >= 2 ? ` · ${t('gov.safety.board.escalated')}` : ''}
          </Typography>
        </Box>
        <StatusChip
          status={EVENT_KIND[event.status]}
          label={t(`gov.safety.status.${event.status}`)}
        />
      </Stack>
      {event.fileId && (
        <Typography variant="body2" sx={{ mt: 1 }}>
          <Link component={RouterLink} to={`/files/${event.fileId}`}>
            {event.fileReference}
          </Link>
          {event.address ? ` — ${event.address}` : ''}
        </Typography>
      )}
      {event.note && (
        <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
          {event.note}
        </Typography>
      )}
      <Box sx={{ mt: 1 }}>
        <Location event={event} />
      </Box>
      {event.acknowledgedAt && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {t('gov.safety.board.acknowledgedBy', {
            name: event.acknowledgedByName ?? '',
            time: shortTime(event.acknowledgedAt, i18n.language),
          })}
        </Typography>
      )}
      {event.resolvedAt && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {t('gov.safety.board.resolvedBy', {
            name: event.resolvedByName ?? '',
            time: shortTime(event.resolvedAt, i18n.language),
          })}
          {event.resolutionNote ? ` — ${event.resolutionNote}` : ''}
        </Typography>
      )}
      {!resolved && (
        <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
          {event.status === 'open' && (
            <Button
              variant="contained"
              color="error"
              size="small"
              disabled={ack.isPending}
              onClick={() =>
                ack.mutate(event.id, {
                  onSuccess: () => toast.success('gov.safety.board.acknowledged'),
                  onError: (err) => toast.error(err, 'gov.safety.board.actionFailed'),
                })
              }
            >
              {t('gov.safety.board.acknowledge')}
            </Button>
          )}
          <Button variant="outlined" size="small" onClick={() => onResolve(event)}>
            {t('gov.safety.board.resolve')}
          </Button>
        </Stack>
      )}
    </HairlineCard>
  );
}

function ResolveDialog({ event, onClose }: { event: SafetyEventView | null; onClose: () => void }) {
  const { t } = useTranslation();
  const toast = useMutationToast();
  const resolve = useResolve();
  const [note, setNote] = useState('');
  const [touched, setTouched] = useState(false);
  useEffect(() => {
    setNote('');
    setTouched(false);
  }, [event?.id]);
  const invalid = note.trim() === '';
  const submit = () => {
    setTouched(true);
    if (!event || invalid) return;
    resolve.mutate(
      { eventId: event.id, note: note.trim() },
      {
        onSuccess: () => {
          toast.success('gov.safety.board.resolved');
          onClose();
        },
        onError: (err) => toast.error(err, 'gov.safety.board.actionFailed'),
      },
    );
  };
  return (
    <Dialog open={!!event} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('gov.safety.board.resolveTitle')}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          label={t('gov.safety.board.resolveNote')}
          helperText={
            touched && invalid
              ? t('gov.safety.board.resolveNoteRequired')
              : t('gov.safety.board.resolveNoteHint')
          }
          error={touched && invalid}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          fullWidth
          multiline
          minRows={3}
          required
          sx={{ mt: 1 }}
          slotProps={{ htmlInput: { maxLength: 1000 } }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.actions.cancel')}</Button>
        <Button variant="contained" onClick={submit} disabled={resolve.isPending}>
          {t('gov.safety.board.resolve')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function TimerRow({ timer, now }: { timer: SafetyTimerView; now: number }) {
  const { t, i18n } = useTranslation();
  const left = Date.parse(timer.dueAt) - now;
  const overdue = timer.status === 'overdue';
  return (
    <TableRow>
      <TableCell>{timer.officerName}</TableCell>
      <TableCell>
        <Link component={RouterLink} to={`/files/${timer.fileId}`}>
          {timer.fileReference}
        </Link>
        <Typography variant="body2" color="text.secondary">
          {timer.address}
        </Typography>
      </TableCell>
      <TableCell>
        {shortTime(timer.dueAt, i18n.language)}
        <Typography variant="body2" color={left < 0 ? 'error' : 'text.secondary'}>
          {left >= 0
            ? t('gov.safety.timer.dueIn', { duration: formatCountdown(left) })
            : t('gov.safety.timer.duePast', { duration: formatCountdown(left) })}
        </Typography>
      </TableCell>
      <TableCell>
        <StatusChip
          status={overdue ? 'emergency' : 'success'}
          label={t(`gov.safety.board.timerStatus.${timer.status}`)}
        />
      </TableCell>
    </TableRow>
  );
}

/**
 * The supervisor's safety board for the municipality in the switcher: alerts
 * to answer, officers on timed visits, and what was resolved in the last day.
 *
 * Live over the socket (the tab joins the municipality's safety room, which the
 * server gates on `safety:respond`) with a 30-second poll as the safety net.
 * Acknowledging stops the escalation clock; resolving needs a note, because the
 * record has to say what happened.
 */
export function SafetyBoard() {
  const { t } = useTranslation();
  const scope = useOptionalScope();
  const lg = scope?.active?.kind === 'local_government' ? scope.active : null;
  const { data, isLoading, isError, refetch } = useSafetyBoard(lg?.id);
  const realtime = useRealtime();
  const realtimeRef = useRef(realtime);
  realtimeRef.current = realtime;
  const lgId = lg?.id;
  useEffect(() => {
    if (!lgId) return;
    return realtimeRef.current.watchSafety(lgId);
  }, [lgId]);
  const now = useNow();
  const [resolving, setResolving] = useState<SafetyEventView | null>(null);
  const offAndEmpty =
    !!data &&
    !data.enabled &&
    data.events.length === 0 &&
    data.timers.length === 0 &&
    data.recent.length === 0;

  return (
    <AppShell>
      <PageIntro>{t('gov.safety.board.subtitle')}</PageIntro>

      {!lg && <Alert severity="info">{t('gov.safety.board.pickMunicipality')}</Alert>}

      {lg && isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
          <CircularProgress />
        </Box>
      )}

      {lg && isError && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => refetch()}>
              {t('common.actions.retry')}
            </Button>
          }
        >
          {t('gov.safety.board.loadError')}
        </Alert>
      )}

      {/* Off with nothing on the board: what the module is and who turns it
          on, instead of three empty sections under a notice. Off with history
          still on it, the history stays readable. */}
      {data && offAndEmpty && (
        <ModuleOffNotice
          icon={Icons.ShieldAlert}
          title={t('gov.safety.board.moduleOff.title')}
          what={t('gov.safety.board.moduleOff.what')}
          who={t('gov.safety.board.moduleOff.who')}
        />
      )}
      {data && !data.enabled && !offAndEmpty && (
        <Alert severity="info">{t('gov.safety.board.disabled')}</Alert>
      )}

      {data && !offAndEmpty && (
        <Stack spacing={3}>
          <Box component="section" aria-labelledby="safety-alerts">
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1 }}>
              <Icons.ShieldAlert size={iconSize.md} aria-hidden />
              <Typography id="safety-alerts" variant="h6">
                {t('gov.safety.board.alerts')}
              </Typography>
            </Stack>
            {data.events.length === 0 ? (
              <Typography color="text.secondary">{t('gov.safety.board.noAlerts')}</Typography>
            ) : (
              <Stack spacing={1.5}>
                {data.events.map((e) => (
                  <EventCard key={e.id} event={e} onResolve={setResolving} />
                ))}
              </Stack>
            )}
          </Box>

          <Box component="section" aria-labelledby="safety-timers">
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1 }}>
              <Icons.Timer size={iconSize.md} aria-hidden />
              <Typography id="safety-timers" variant="h6">
                {t('gov.safety.board.timers')}
              </Typography>
            </Stack>
            {data.timers.length === 0 ? (
              <Typography color="text.secondary">{t('gov.safety.board.noTimers')}</Typography>
            ) : (
              <HairlineCard sx={{ p: 0, overflowX: 'auto' }}>
                <Table size="small" aria-labelledby="safety-timers">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('gov.safety.board.columns.officer')}</TableCell>
                      <TableCell>{t('gov.safety.board.columns.file')}</TableCell>
                      <TableCell>{t('gov.safety.board.columns.due')}</TableCell>
                      <TableCell>{t('gov.safety.board.columns.status')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.timers.map((timer) => (
                      <TimerRow key={timer.id} timer={timer} now={now} />
                    ))}
                  </TableBody>
                </Table>
              </HairlineCard>
            )}
          </Box>

          <Box component="section" aria-labelledby="safety-recent">
            <Typography id="safety-recent" variant="h6" sx={{ mb: 1 }}>
              {t('gov.safety.board.recent')}
            </Typography>
            {data.recent.length === 0 ? (
              <Typography color="text.secondary">{t('gov.safety.board.noRecent')}</Typography>
            ) : (
              <Stack spacing={1.5}>
                {data.recent.map((e) => (
                  <EventCard key={e.id} event={e} onResolve={setResolving} />
                ))}
              </Stack>
            )}
          </Box>

          <Typography variant="body2" color="text.secondary">
            {t('gov.safety.privacyNote')}
          </Typography>
        </Stack>
      )}

      <ResolveDialog event={resolving} onClose={() => setResolving(null)} />
    </AppShell>
  );
}
